
        // Inline browser environment setup for Vite compatibility - PARALLEL LOADING
        const [
            { default: BrowserEnvironment },
            { WebPasswordManager },
            { BrowserHyperliquidClient },
            { BrowserPositionTracker },
            { BrowserOrderbookTracker },
            { BrowserTradesTracker },
            { ChaseOrderbookService },
            { BrowserCandleTracker },
            { BrowserPriceService }
        ] = await Promise.all([
            import('@browser/environment.js'),
            import('@browser/services/password-manager.js'),
            import('@browser/services/hyperliquid-client.js'),
            import('@browser/services/position-tracker.js'),
            import('@browser/services/orderbook-tracker.js'),
            import('@browser/services/trades-tracker.js'),
            import('@browser/services/chase-orderbook-service.js'),
            import('@browser/services/candle-tracker.js'),
            import('@browser/services/price-service.js')
        ]);

        // Set up browser environment
        window.Environment = BrowserEnvironment;
        // console.log('✅ Browser Environment loaded:', window.Environment);

        // Mock electronAPI for compatibility with Auto Trade Bot support
        window.electronAPI = {
            invoke: async (channel, ...args) => {
                // Auto Trade Bot IPC compatibility layer
                if (channel === 'autoTrade:getStatus') {
                    return window.BrowserAutoTradeBot ? window.BrowserAutoTradeBot.getStatus() : { success: false };
                }
                if (channel === 'autoTrade:toggle') {
                    return window.BrowserAutoTradeBot ? window.BrowserAutoTradeBot.toggle(args[0]) : { success: false };
                }
                if (channel === 'autoTrade:updateSettings') {
                    return window.BrowserAutoTradeBot ? window.BrowserAutoTradeBot.updateSettings(args[0]) : { success: false };
                }

                // Hyperliquid service IPC compatibility
                if (channel === 'hyperliquid:getUserState') {
                    if (window.BrowserHyperliquidClient && window.BrowserHyperliquidClient.isInitialized) {
                        // console.log('🔧 [IPC Shim] Routing hyperliquid:getUserState to BrowserHyperliquidClient');
                        const userState = await window.BrowserHyperliquidClient.getUserState();
                        return { success: true, userState: userState };
                    }
                    console.warn('⚠️ [IPC Shim] BrowserHyperliquidClient not initialized for getUserState');
                    return { success: false, error: 'BrowserHyperliquidClient not initialized' };
                }
                if (channel === 'hyperliquid:executeQuickTrade') {
                    if (window.BrowserHyperliquidClient && window.BrowserHyperliquidClient.isInitialized) {
                        // console.log('🔧 [IPC Shim] Routing hyperliquid:executeQuickTrade to BrowserHyperliquidClient');
                        return await window.BrowserHyperliquidClient.placeQuickTradeWithTPSL(args[0]);
                    }
                    console.warn('⚠️ [IPC Shim] BrowserHyperliquidClient not initialized for executeQuickTrade');
                    return { success: false, error: 'BrowserHyperliquidClient not initialized' };
                }

                // Bot cancellation status (browser mode)
                if (channel === 'bot:getCancellationStatus') {
                    if (window.BrowserCancellationBot) {
                        const status = window.BrowserCancellationBot.getStatus();
                        return { success: true, status };
                    }
                    return { success: false };
                }

                // Trailing SL Bot IPC compatibility
                if (channel === 'trailingSL:getStatus') {
                    if (window.BrowserTrailingSLBot) {
                        const status = window.BrowserTrailingSLBot.getStatus();
                        return { success: true, status };
                    }
                    return { success: false, error: 'Trailing SL bot not initialized' };
                }
                if (channel === 'trailingSL:toggle') {
                    if (window.BrowserTrailingSLBot) {
                        const enabled = window.BrowserTrailingSLBot.toggle();
                        return { success: true, enabled };
                    }
                    return { success: false, error: 'Trailing SL bot not initialized' };
                }
                if (channel === 'trailingSL:updateSettings') {
                    if (window.BrowserTrailingSLBot) {
                        await window.BrowserTrailingSLBot.updateSettings(args[0]);
                        return { success: true };
                    }
                    return { success: false, error: 'Trailing SL bot not initialized' };
                }

                // Iceberg order IPC compatibility
                if (channel === 'iceberg:start') {
                    // console.log('🔧 [IPC Shim] Routing iceberg:start to BrowserIcebergService', args[0]);
                    if (window.BrowserIcebergService) {
                        const result = await window.BrowserIcebergService.startIceberg(args[0].orderId, args[0].settings);
                        // console.log('🔧 [IPC Shim] iceberg:start result:', result);
                        return result;
                    }
                    console.warn('⚠️ [IPC Shim] BrowserIcebergService not initialized');
                    return { success: false, error: 'Iceberg service not initialized' };
                }
                if (channel === 'iceberg:stop') {
                    if (window.BrowserIcebergService) {
                        return await window.BrowserIcebergService.stopIceberg(args[0].icebergId, args[0].reason);
                    }
                    return { success: false };
                }
                if (channel === 'iceberg:getActive') {
                    if (window.BrowserIcebergService) {
                        const icebergs = window.BrowserIcebergService.getActiveIcebergs();
                        return { success: true, icebergs };
                    }
                    return { success: true, icebergs: [] };
                }
                if (channel === 'iceberg:getStatus') {
                    if (window.BrowserIcebergService) {
                        const status = window.BrowserIcebergService.getIcebergStatus(args[0].icebergId);
                        return { success: true, status };
                    }
                    return { success: false };
                }
                if (channel === 'iceberg:saveSettings') {
                    if (window.BrowserIcebergService) {
                        window.BrowserIcebergService.storage.saveGlobalSettings(args[0]);
                        return { success: true };
                    }
                    return { success: false };
                }
                if (channel === 'iceberg:loadSettings') {
                    if (window.BrowserIcebergService) {
                        const settings = window.BrowserIcebergService.storage.loadGlobalSettings();
                        return { success: true, settings };
                    }
                    return { success: false };
                }

                // For truly unknown IPC calls, log warning
                console.warn(`⚠️ IPC call blocked: ${channel}`, args);
                throw new Error(`Use Browser services instead of IPC for: ${channel}`);
            },
            on: () => { }, // console.warn('⚠️ IPC not available in web'),
            removeListener: () => { },
            crypto: undefined,
            window: { toggleFullscreen: () => { } }
        };

        window.process = window.process || {
            env: { NODE_ENV: 'production' },
            versions: { electron: undefined }
        };

        // console.log('✅ Web shim loaded - browser services ready');

        // Signal that browser shim is ready
        window._browserShimReady = true;
        window.dispatchEvent(new Event('browserShimReady'));
    